import { t } from "@lingui/macro";
import { useLinkBuilder, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import * as Clipboard from "expo-clipboard";
import { Platform, Share } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { ShareIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { getExpoEASConstant } from "../../../../utils/expoConstants";
import { EventDetailTitleRow$key } from "./__generated__/EventDetailTitleRow.graphql";

type EventDetailTitleRowProps = {
  fragmentRef: EventDetailTitleRow$key;
};

const getAppUrl = () => {
  const env = getExpoEASConstant("deployEnv");
  if (env === "prod") {
    return "https://app.whoosh.io";
  } else if (env === "replica") {
    return "https://replica.whoosh.io";
  } else if (env === "staging") {
    return "https://staging.whoosh.io";
  } else if (env === "qa") {
    return "https://qa.whoosh.io";
  } else if (env === "demo") {
    return "https://demo.whoosh.io";
  } else if (env === "dev") {
    return "https://dev.whoosh.io";
  } else if (env === "local") {
    return "http://localhost:8081";
  } else {
    return "https://app.whoosh.io";
  }
};

export const EventDetailTitleRow = ({
  fragmentRef,
}: EventDetailTitleRowProps) => {
  const data = useFragment(EventDetailTitleRowFragment, fragmentRef);
  const { showToast } = useToast();
  const { buildHref } = useLinkBuilder();
  const route = useRoute();

  const copyEventUrl = async () => {
    const isLocal =
      Platform.OS === "web" && window.location.host.includes("localhost");
    const protocol = isLocal ? "http" : "https";

    const appUrl = getAppUrl();
    const link =
      Platform.OS === "web"
        ? isLocal
          ? `${protocol}://localhost:8081${buildHref(route.name, route.params)}`
          : `${protocol}://${window.location.host}${buildHref(
              route.name,
              route.params
            )}`
        : `${appUrl}${buildHref(route.name, route.params)}`;
    if (link) {
      if (Platform.OS === "web") {
        await Clipboard.setStringAsync(link);
        showToast({
          description: t`Copied Event URL`,
          semanticStyle: "success",
          toastId: "copy-event-url",
        });
      } else {
        Share.share({ url: link });
      }
    }
  };

  return (
    <HStack alignItems="center" gap={5} width="100%">
      <Box alignSelf="center">
        <Box
          style={{ borderRadius: 2 }}
          width={8}
          height={8}
          backgroundColor={`surface-user-selectable-color${data.eventSeries.color}`}
        />
      </Box>
      <Text
        flex={1}
        variant="mainTitle3"
        color="text-primary"
        testID="event-detail-title"
      >
        {data.eventSeries.name}
      </Text>
      <Button
        variant="white"
        size="small"
        onPress={copyEventUrl}
        leftIcon={<ShareIcon />}
        accessibilityLabel={t`Share button`}
        testID="event-detail-share-button"
      />
    </HStack>
  );
};

export const EventDetailTitleRowFragment = graphql`
  fragment EventDetailTitleRow on Event {
    eventSeries {
      name
      color
    }
  }
`;
